<template>
  <v-container fluid class="py-8">
    <!-- Header -->
    <v-row class="mb-4" align="center" justify="space-between">
      <v-col cols="auto">
        <span class="text-overline text-medium-emphasis d-block mb-1">Agents</span>
        <h1 class="text-h5 font-weight-bold">Pending Agent Signups</h1>
      </v-col>
      <v-col cols="auto">
        <!-- (optional) space for future buttons -->
      </v-col>
    </v-row>

    <!-- Loading skeleton -->
    <v-card v-if="isLoading" elevation="2" class="mb-6">
      <v-card-title>Pending</v-card-title>
      <v-card-text>
        <!-- header skeleton -->
        <div class="d-flex mb-2">
          <v-skeleton-loader type="text" width="18%" class="mr-4" />
          <v-skeleton-loader type="text" width="18%" class="mr-4" />
          <v-skeleton-loader type="text" width="16%" class="mr-4" />
          <v-skeleton-loader type="text" width="14%" class="mr-4" />
          <v-skeleton-loader type="text" width="18%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="ml-auto" />
        </div>
        <!-- rows skeleton -->
        <div v-for="n in pageSize" :key="n" class="d-flex align-center py-3">
          <v-skeleton-loader type="text" width="20%" class="mr-4" />
          <v-skeleton-loader type="text" width="18%" class="mr-4" />
          <v-skeleton-loader type="text" width="18%" class="mr-4" />
          <v-skeleton-loader type="text" width="14%" class="mr-4" />
          <v-skeleton-loader type="text" width="16%" class="mr-4" />
          <v-skeleton-loader type="button" width="140" class="ml-auto" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Real table -->
    <v-card v-else elevation="2" class="mb-6">
      <v-card-title>Pending</v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th class="text-left">Applicant</th>
              <th class="text-left">Agency</th>
              <th class="text-left">Email</th>
              <th class="text-left">Phone</th>
              <th class="text-left">Location</th>
              <th class="text-left">Created</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="p in pending" :key="p.id">
              <!-- Applicant -->
              <td>
                <div class="text-body-2 font-weight-medium">
                  {{ fullName(p) }}
                </div>
                <div class="text-caption text-medium-emphasis">ID: {{ p.id }}</div>
              </td>

              <!-- Agency -->
              <td>{{ p.agency_name || '—' }}</td>

              <!-- Email -->
              <td>{{ p.email || '—' }}</td>

              <!-- Phone -->
              <td>{{ p.phone || '—' }}</td>

              <!-- Location -->
              <td>{{ locationStr(p) }}</td>

              <!-- Created -->
              <td>{{ formatDate(p.created_at) }}</td>

              <!-- Actions -->
              <td class="text-right">
                <v-btn size="small" variant="outlined" color="grey" @click="view(p)">
                  View
                </v-btn>
              </td>
            </tr>

            <tr v-if="(pending || []).length === 0">
              <td colspan="8" class="text-center text-medium-emphasis py-6">
                No pending signups.
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" justify="space-between" align="center">
          <v-col cols="auto">
            <small class="text-medium-emphasis">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
            </small>
          </v-col>
          <v-col cols="auto">
            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="5" prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right" @update:modelValue="goToPage" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="2500">
      {{ snackbar.msg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'

const DOCS_BUCKET = 'agent-documents' // private

export default {
  name: 'PendingAgents',

  data() {
    return {
      pending: [],
      isLoading: true,

      pageSize: 10,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,

      snackbar: { show: false, color: 'error', msg: '' },
      authListener: null,
    }
  },

  computed: {
    pageStart() { return (this.currentPage - 1) * this.pageSize },
    pageEnd() { return Math.min(this.pageStart + this.pageSize, this.totalCount || 0) },
  },

  methods: {
    toast(msg, color = 'error') {
      this.snackbar.msg = msg
      this.snackbar.color = color
      this.snackbar.show = true
    },

    fullName(p) {
      const fn = (p?.first_name || '').trim()
      const ln = (p?.last_name || '').trim()
      return fn || ln ? [fn, ln].filter(Boolean).join(' ') : (p?.email || '—')
    },

    locationStr(p) {
      const parts = [p?.street, p?.city, p?.region, p?.postal_code].filter(Boolean)
      return parts.length ? parts.join(', ') : '—'
    },

    formatDate(d) {
      if (!d) return '—'
      const dt = new Date(d)
      return isNaN(dt) ? '—' : dt.toLocaleDateString()
    },

    /**
     * Wait until Supabase has a valid session (initial restore/refresh can be async).
     * Resolves early if already available, otherwise races onAuthStateChange + polling.
     */
    async waitForSessionReady(maxMs = 5000) {
      // fast path
      let { data } = await supabase.auth.getSession()
      if (data?.session) return

      // slow path
      const start = Date.now()
      await new Promise((resolve) => {
        let intervalId = null
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
          if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
            if (intervalId) clearInterval(intervalId)
            subscription.unsubscribe()
            resolve()
          }
        })
        intervalId = setInterval(async () => {
          const { data } = await supabase.auth.getSession()
          const timedOut = Date.now() - start > maxMs
          if (data?.session || timedOut) {
            clearInterval(intervalId)
            subscription.unsubscribe()
            resolve()
          }
        }, 250)
      })
    },

    /**
     * Fetch pending signups with a single retry if we hit a session/auth race (401/JWT).
     */
    async fetchPending(page = 1, attempt = 1) {
      this.isLoading = true
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1

      try {
        const { data, count, error } = await supabase
          .from('pending_agent_signups')
          .select('*', { count: 'exact' })
          .order('created_at', { ascending: false })
          .range(from, to)

        if (error) throw error

        this.pending = data || []
        this.totalCount = count ?? this.pending.length
        this.totalPages = Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize))
      } catch (err) {
        const msg = (err?.message || err?.error_description || '').toLowerCase()
        const isAuthRace = err?.status === 401 || /jwt|auth|unauth/.test(msg)
        if (attempt === 1 && isAuthRace) {
          await this.waitForSessionReady()
          return this.fetchPending(page, 2)
        }

        console.error('fetchPending error:', err)
        this.pending = []
        this.totalCount = 0
        this.totalPages = 1
        this.toast('Failed to load pending signups.')
      } finally {
        this.isLoading = false
      }
    },

    goToPage(page) {
      if (page === this.currentPage) return
      this.currentPage = page
      this.fetchPending(page)
    },

    async openDoc(pathOrUrl) {
      if (!pathOrUrl) return
      try {
        if (/^https?:\/\//i.test(pathOrUrl)) {
          window.open(pathOrUrl, '_blank', 'noopener,noreferrer')
          return
        }
        // Assume it's a private Storage path inside our bucket
        const { data, error } = await supabase
          .storage
          .from(DOCS_BUCKET)
          .createSignedUrl(pathOrUrl, 60 * 60) // 1 hour
        if (error) throw error
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
      } catch (err) {
        console.error('openDoc error:', err)
        this.toast('Could not open document.')
      }
    },

    view(p) {
      this.$router.push({ path: `/dashboard/pending-agents/${p.id}` })
    },
  },

  /**
   * Route guards ensure we fetch when navigating here without a full reload.
   */
  async beforeRouteEnter(to, from, next) {
    next(async (vm) => {
      await vm.waitForSessionReady()
      await vm.fetchPending(vm.currentPage)
    })
  },

  async beforeRouteUpdate(to, from, next) {
    await this.waitForSessionReady()
    await this.fetchPending(this.currentPage)
    next()
  },

  /**
   * Refresh when auth state changes (e.g., token refresh, re-login).
   */
  created() {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        this.fetchPending(this.currentPage)
      }
    })
    this.authListener = subscription
  },

  /**
   * If parent uses <keep-alive>, this runs when the cached view becomes active.
   */
  activated() {
    this.fetchPending(this.currentPage)
  },

  beforeUnmount() {
    this.authListener?.unsubscribe?.()
  },
}
</script>


<style scoped>
.v-table thead th {
  font-weight: 600;
}
</style>
